// Respuesta con IA de cara al cliente — panel de la ficha del ticket (staff).
// Muestra qué hizo la primera respuesta y la conversación, la marca «sin IA»
// del ticket y el botón «Reactivar» (sólo un aprobador lo puede usar: el
// backend lo exige). Qué se muestra lo decide el módulo puro aiReplyPanel.ts.
import { useTranslation } from "react-i18next";
import type { Ticket } from "../services/serviceDeskService";
import { getConversationView, getFirstReplyView, type FirstReplyTone } from "../services/aiReplyPanel";

interface Props {
  ticket: Pick<Ticket, "aiAgent" | "aiConversation" | "aiFirstReplyDisabled" | "status">;
  isDarkMode: boolean;
  primaryColor: string;
  saving?: boolean;
  onToggleDisabled: (disabled: boolean) => void;
  onResume: () => void;
}

const TONE: Record<FirstReplyTone, string> = {
  ok: "bg-green-50 text-green-700 dark:bg-green-500/10",
  info: "bg-blue-50 text-blue-700 dark:bg-blue-500/10",
  warn: "bg-amber-50 text-amber-700 dark:bg-amber-500/10",
  error: "bg-red-50 text-red-600 dark:bg-red-500/10",
  muted: "bg-gray-50 text-gray-600 dark:bg-gray-500/10",
};

export function AiReplyPanel({ ticket, isDarkMode, primaryColor, saving, onToggleDisabled, onResume }: Props) {
  const { t } = useTranslation("serviceDesk");
  const first = getFirstReplyView(ticket);
  const conv = getConversationView(ticket);
  const text = isDarkMode ? "text-white" : "text-gray-900";
  const muted = isDarkMode ? "text-gray-400" : "text-gray-500";
  const card = isDarkMode ? "bg-dark-surface border-dark-border" : "bg-white border-gray-200";

  return (
    <div className={`rounded-xl border p-5 ${card}`} data-testid="ai-reply-panel">
      <h2 className={`mb-3 text-sm font-semibold ${text}`}>{t("ticketDetail.aiReply.title")}</h2>

      {first.visible && (
        <div className={`mb-3 rounded-lg p-3 text-xs ${TONE[first.tone]}`}>
          <span className="block font-semibold">{t("ticketDetail.aiReply.first.title")}</span>
          <span className="block">{t(first.messageKey)}</span>
          {first.scopeKey && <span className="mt-1 block opacity-80">{t(first.scopeKey)}</span>}
          {first.error && first.state === "failed" && <span className="mt-1 block font-mono">{first.error}</span>}
        </div>
      )}

      {conv.visible && (
        <div className="mb-3">
          <span className={`block text-xs font-semibold ${text}`}>{t("ticketDetail.aiReply.conversation.title")}</span>
          <dl className={`mt-1 space-y-1 text-xs ${muted}`}>
            <div className="flex justify-between gap-2">
              <dt>{t("ticketDetail.aiReply.conversation.turns")}</dt>
              <dd className={text}>{conv.turns}</dd>
            </div>
            <div className="flex justify-between gap-2">
              <dt>{t("ticketDetail.aiReply.conversation.cost")}</dt>
              <dd className={text}>US$ {conv.costUsd.toFixed(4)}</dd>
            </div>
          </dl>
          {conv.pausedByBot && <p className={`mt-2 text-xs ${muted}`}>{t("ticketDetail.aiReply.conversation.paused")}</p>}
          {conv.handedOff && (
            <p className={`mt-2 rounded-lg p-2 text-xs ${TONE.warn}`}>
              {t("ticketDetail.aiReply.conversation.handedOff")}
              {conv.reasonKey && <span className="mt-1 block">{t(conv.reasonKey)}</span>}
            </p>
          )}
          {!conv.handedOff && conv.humanOwns && (
            <p className={`mt-2 text-xs ${muted}`}>{t("ticketDetail.aiReply.conversation.humanOwns")}</p>
          )}
          {conv.canResume && (
            <button
              type="button"
              onClick={onResume}
              disabled={saving}
              className="mt-2 rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
              style={{ backgroundColor: primaryColor }}
            >
              {t("ticketDetail.aiReply.conversation.resume")}
            </button>
          )}
        </div>
      )}

      <label className={`flex items-center gap-2 text-sm ${text}`}>
        <input
          type="checkbox"
          checked={ticket.aiFirstReplyDisabled === true}
          disabled={saving}
          onChange={(e) => onToggleDisabled(e.target.checked)}
        />
        {t("ticketDetail.aiReply.first.disableToggle")}
      </label>
      <p className={`mt-1 text-[11px] ${muted}`}>{t("ticketDetail.aiReply.first.disableHint")}</p>
    </div>
  );
}
